<script setup lang="ts">
import { CHART_TYPES, ChartType, TIME_SERIES_CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { computed, ComputedRef, type FunctionalComponent, ref, Ref, watch } from 'vue'
import BaseCreator from '@/components/charts/chart-creator/BaseCreator.vue'
import {
  BaseSerializedChart,
  BaseSerializedInfluxChart,
  BaseSerializedInfluxLineChart,
  SerializedCameraBasedChart,
  SerializedChartType,
  SerializedIRChart,
  SerializedRegionChart,
  SerializedRegionSumChart,
} from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import LineCreator from '@/components/charts/chart-creator/LineCreator.vue'
import { assemble } from '@/composables/charts/influx-charts/chart-builders/ChartFactory'
import TimeSetupCreator from '@/components/charts/chart-creator/TimeSetupCreator.vue'
import { SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import CameraBasedCreator from '@/components/charts/chart-creator/CameraBasedCreator.vue'
import RegionCreator from '@/components/charts/chart-creator/RegionCreator.vue'
import RegionSumCreator from '@/components/charts/chart-creator/RegionSumCreator.vue'
import IRChartCreator from '@/components/charts/chart-creator/IRChartCreator.vue'
import ExpandableCard from '@/components/ExpandableCard.vue'
import _ from 'lodash'
import { InfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/InfluxLineChartBuilder'
import { CameraBasedInfluxLineChartBuilder } from '@/composables/charts/influx-charts/chart-builders/CameraBasedInfluxLineChartBuilder'
import { RegionInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionChartBuilder'
import { RegionSumInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionSumChartBuilder'
import { IRInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IRChartBuilder'
import AnnotatorCreator from '@/components/charts/chart-creator/AnnotatorCreator.vue'
import { SerializedChartAnnotatorType } from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import IncidentReductionCreator from '@/components/charts/chart-creator/IncidentReductionCreator.vue'
import { SerializedIncidentReducer } from '@/composables/charts/influx-charts/incident-reducers/IncidentReducers'

const model = defineModel<SerializedChartType | null | undefined>()
withDefaults(defineProps<{ showPreview: boolean }>(), { showPreview: true })

const baseSerialized: Ref<BaseSerializedChart | null> = ref(null)
const lineSerialized: Ref<BaseSerializedInfluxLineChart | null> = ref(null)
const cameraBasedSerialized: Ref<SerializedCameraBasedChart | null> = ref(null)
const regionSerialized: Ref<SerializedRegionChart | null> = ref(null)
const regionSumSerialized: Ref<SerializedRegionSumChart | null> = ref(null)
const irSerialized: Ref<SerializedIRChart | null> = ref(null)
const serializedAnnotators: Ref<SerializedChartAnnotatorType[]> = ref([])
const serializedIncidentReducer: Ref<SerializedIncidentReducer | undefined> = ref(undefined)
const timeSetup: Ref<SerializedGeneralChartTimeSetup | null> = ref(null)

const serialized: ComputedRef<SerializedChartType | null> = computed(() => {
  let result: SerializedChartType | null = null
  if (baseSerialized.value?.chartType === CHART_TYPES.LINE) result = lineSerialized.value
  if (baseSerialized.value?.chartType === CHART_TYPES.CAMERA_BASED_LINE) result = cameraBasedSerialized.value
  if (baseSerialized.value?.chartType === CHART_TYPES.REGIONS) result = regionSerialized.value
  if (baseSerialized.value?.chartType === CHART_TYPES.REGION_SUMS) result = regionSumSerialized.value
  if (baseSerialized.value?.chartType === CHART_TYPES.IR) result = irSerialized.value

  if (
    result &&
    [CHART_TYPES.LINE, CHART_TYPES.CAMERA_BASED_LINE, CHART_TYPES.REGIONS, CHART_TYPES.REGION_SUMS, CHART_TYPES.IR].includes(result.chartType)
  ) {
    const influxResult = result as BaseSerializedInfluxChart
    influxResult.incidentAnnotators = serializedAnnotators.value
    influxResult.incidentReducer = serializedIncidentReducer.value
  }
  return result
})

const chartComponent: ComputedRef<FunctionalComponent | null> = computed(() => {
  if (!serialized.value) return null
  try {
    return assemble(serialized.value)
  } catch {
    return null
  }
})

const showTimeSetup = computed(() => {
  if (!baseSerialized.value) return false
  return TIME_SERIES_CHART_TYPES.includes(baseSerialized.value.chartType as ChartType)
})

const showLineSetup = computed(() => {
  if (!baseSerialized.value) return false
  return baseSerialized.value.chartType === CHART_TYPES.LINE
})

const showCameraBasedSetup = computed(() => {
  if (!baseSerialized.value) return false
  return baseSerialized.value.chartType === CHART_TYPES.CAMERA_BASED_LINE
})

const showRegionSetup = computed(() => {
  if (!baseSerialized.value) return false
  return baseSerialized.value.chartType === CHART_TYPES.REGIONS
})

const showRegionSumSetup = computed(() => {
  if (!baseSerialized.value) return false
  return baseSerialized.value.chartType === CHART_TYPES.REGION_SUMS
})

const showIRSetup = computed(() => {
  if (!baseSerialized.value) return false
  return baseSerialized.value.chartType === CHART_TYPES.IR
})

const showAnnotatorSetup = computed(() => {
  if (!baseSerialized.value) return false
  return ([CHART_TYPES.LINE, CHART_TYPES.CAMERA_BASED_LINE, CHART_TYPES.REGIONS, CHART_TYPES.REGION_SUMS] as ChartType[]).includes(
    baseSerialized.value.chartType
  )
})

const showIncidentReducerSetup = computed(() => {
  if (!baseSerialized.value) return false
  return ([CHART_TYPES.LINE, CHART_TYPES.CAMERA_BASED_LINE, CHART_TYPES.REGIONS, CHART_TYPES.REGION_SUMS] as ChartType[]).includes(
    baseSerialized.value.chartType
  )
})

watch(
  serialized,
  () => {
    if (!serialized.value) return
    if (_.isEqual(serialized.value, model.value)) return
    model.value = serialized.value
  },
  { deep: true, immediate: true }
)

watch(
  model,
  () => {
    if (_.isEqual(serialized.value, model.value)) return
    if (!model.value) return
    baseSerialized.value = {
      chartType: model.value.chartType,
      title: model.value.title,
      subtitle: model.value.subtitle,
      chartEntity: model.value.chartEntity,
      defaultCollapsed: model.value.defaultCollapsed,
      height: model.value.height,
      seriesBasedHeight: model.value.seriesBasedHeight,
      ...(model.value.yAxisType && { yAxisType: model.value.yAxisType }),
    }
    timeSetup.value = model.value.timeSetup

    if (
      ([CHART_TYPES.LINE, CHART_TYPES.CAMERA_BASED_LINE, CHART_TYPES.REGIONS, CHART_TYPES.REGION_SUMS, CHART_TYPES.IR] as ChartType[]).includes(
        model.value.chartType
      )
    ) {
      const influxModel = model.value as BaseSerializedInfluxChart
      if (influxModel.incidentAnnotators) serializedAnnotators.value = influxModel.incidentAnnotators
      if (influxModel.incidentReducer) serializedIncidentReducer.value = influxModel.incidentReducer
    }
    if (model.value.chartType === CHART_TYPES.LINE)
      lineSerialized.value = new InfluxLineChartBuilder(model.value as BaseSerializedInfluxLineChart).serialize()
    if (model.value.chartType === CHART_TYPES.CAMERA_BASED_LINE)
      cameraBasedSerialized.value = new CameraBasedInfluxLineChartBuilder(model.value as SerializedCameraBasedChart).serialize()
    if (model.value.chartType === CHART_TYPES.REGIONS)
      regionSerialized.value = new RegionInfluxChartBuilder(model.value as SerializedRegionChart).serialize()
    if (model.value.chartType === CHART_TYPES.REGION_SUMS)
      regionSumSerialized.value = new RegionSumInfluxChartBuilder(model.value as SerializedRegionSumChart).serialize()
    if (model.value.chartType === CHART_TYPES.IR) irSerialized.value = new IRInfluxChartBuilder(model.value as SerializedIRChart).serialize()
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <BaseCreator v-model="baseSerialized" />

  <v-divider
    v-if="showTimeSetup"
    class="my-8"
    thickness="2"
  />

  <TimeSetupCreator
    v-if="showTimeSetup"
    v-model="timeSetup"
  />

  <v-divider
    v-if="showLineSetup"
    class="my-8"
    thickness="2"
  />

  <LineCreator
    v-if="showLineSetup"
    v-model="lineSerialized"
    :time-setup="timeSetup"
    :base-serialized="baseSerialized"
  />

  <v-divider
    v-if="showCameraBasedSetup"
    class="my-8"
    thickness="2"
  />

  <CameraBasedCreator
    v-if="showCameraBasedSetup"
    v-model="cameraBasedSerialized"
    :time-setup="timeSetup"
    :base-serialized="baseSerialized"
  />

  <v-divider
    v-if="showRegionSetup"
    class="my-8"
    thickness="2"
  />

  <RegionCreator
    v-if="showRegionSetup"
    v-model="regionSerialized"
    :time-setup="timeSetup"
    :base-serialized="baseSerialized"
  />

  <v-divider
    v-if="showRegionSumSetup"
    class="my-8"
    thickness="2"
  />

  <RegionSumCreator
    v-if="showRegionSumSetup"
    v-model="regionSumSerialized"
    :base-serialized="baseSerialized"
    :time-setup="timeSetup"
  />

  <v-divider
    v-if="showIRSetup"
    class="my-8"
    thickness="2"
  />

  <IRChartCreator
    v-if="showIRSetup"
    v-model="irSerialized"
    :base-serialized="baseSerialized"
    :time-setup="timeSetup"
  />

  <v-divider
    v-if="showAnnotatorSetup"
    class="my-8"
    thickness="2"
  />

  <AnnotatorCreator
    v-if="showAnnotatorSetup"
    v-model="serializedAnnotators"
  />

  <v-divider
    v-if="showIncidentReducerSetup"
    class="my-8"
    thickness="2"
  />

  <IncidentReductionCreator
    v-if="showIncidentReducerSetup"
    v-model="serializedIncidentReducer"
  />

  <v-divider
    v-if="showPreview"
    class="my-8"
    thickness="2"
  />

  <ExpandableCard
    v-if="showPreview"
    :model-value="false"
    class="mx-2 mb-2 mt-10"
  >
    <template #title>
      <v-card-title>{{ $t('chart_sharing.creator.customize.preview') }}</v-card-title>
    </template>
    <template #text>
      <component
        :is="chartComponent"
        v-if="chartComponent"
      ></component>
    </template>
  </ExpandableCard>
</template>
<style scoped></style>
